import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './AiCostOverview.css';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];

// Lesbare Labels für die creditLog-Aktionen.
const ACTION_LABELS = {
  email_generation: 'Email (Standard)',
  hyperintelligence_email: 'Email (Hyperintelligence)',
  hyperintelligence_pdf_email: 'Email (Hyper + PDF)',
  outreach_job_email: 'Email (Outreach)',
  bulk_email_batch: 'Email (Bulk Batch)',
  voice_clone_send: 'Voice Clone Send',
  voice_clone_preview: 'Voice Clone Preview',
  salesgod_writer: 'SalesGod Writer',
  salesgod_writer_expand: 'SalesGod Expand',
  web_research: 'Web Research',
  potential_analyzer: 'Potential Analyzer',
  website_analyzer: 'Website Analyzer',
  instagram_research: 'Instagram Research',
  seo_analyzer: 'SEO Analyzer',
  dm_email_finder: 'DM Email Finder',
  dm_email_finder_deep: 'DM Email Finder (Deep)',
  contact_clean: 'Contact Clean',
  outreach_job_clean: 'Contact Clean (Outreach)',
  outreach_job_ig_research: 'Instagram Research (Outreach)',
  google_review_audit: 'Google Review Audit',
  outreach_job_review_audit: 'Google Review Audit (Outreach)',
  dynamic_image: 'Image Lab (AI-Bild)',
  value_based_offer: 'Value-Based Offer',
  offer_intake: 'Offer Intake',
  company_research: 'Company Research',
  spam_check: 'Spam-Check',
  inbox_agent_reply: 'Inbox AI-Antwort',
  inbox_agent_process: 'Inbox AI-Verarbeitung',
  // Nachbelastung eines Bulk-Batches, dessen Vorab-Abbuchung zu niedrig war.
  // Enthält keine eigenen KI-Kosten — die stecken schon im Batch.
  bulk_email_topup: 'Email (Bulk Nachbelastung)',
};

// Feste Reihenfolge + Farbe pro KI-Anbieter für die Chips.
const PROVIDER_ORDER = ['Claude', 'ChatGPT', 'Grok', 'DeepSeek', 'Perplexity', 'Gemini', 'Sonstige'];

const eur = (n) =>
  (Number(n) || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });

const sortedProviders = (byProvider) =>
  Object.entries(byProvider || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => {
      const ai = PROVIDER_ORDER.indexOf(a[0]);
      const bi = PROVIDER_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

const ProviderChips = ({ byProvider }) => {
  const entries = sortedProviders(byProvider);
  if (entries.length === 0) return <span className="aico__muted">–</span>;
  return (
    <div className="aico__providers">
      {entries.map(([provider, value]) => (
        <span key={provider} className={`aico__provChip aico__prov--${provider.toLowerCase()}`}>
          {provider} {eur(value)}
        </span>
      ))}
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

function AiCostOverview() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (windowDays) => {
    setIsLoading(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'getAiCostOverviewAdmin');
      const res = await fn({ days: windowDays });
      setData(res.data || null);
    } catch (err) {
      console.error('[AiCostOverview] load failed:', err);
      setError(err?.message || 'Fehler beim Laden der KI-Kosten.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const accounts = data?.accounts || [];
  const totals = data?.totals || { accounts: 0, estimatedCostEur: 0, creditsSpent: 0, events: 0 };
  const topAccount = accounts[0] || null;

  const maxCost = useMemo(
    () => accounts.reduce((m, a) => Math.max(m, a.estimatedCostEur || 0), 0),
    [accounts]
  );

  const toggle = (uid) => setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const labelFor = (action) => ACTION_LABELS[action] || action;

  return (
    <div className="aico">
      <div className="aico__head">
        <div>
          <h2 className="aico__title">
            <i className="fa fa-microchip" /> AI Cost Overview
          </h2>
          <p className="aico__subtitle">
            Geschätzte echte KI-Kosten pro aktivem Account (Login in den letzten {days} Tagen).
            <span
              className="aico__estimateBadge"
              title="Schätzung: Es gibt kein echtes Token-Logging. Die Kosten werden aus dem Credit-Log abgeleitet, indem jede Aktion auf das eingesetzte KI-Modell + ein typisches Token-Volumen gemappt und über bekannte Modellpreise umgerechnet wird. Die Annahmen liegen zentral in functions/aiCostEstimates.js."
            >
              <i className="fa fa-circle-info" /> Schätzung
            </span>
          </p>
        </div>
        <div className="aico__controls">
          <label className="aico__windowLabel">Zeitfenster</label>
          <div className="aico__windowBtns">
            {WINDOW_OPTIONS.map((d) => (
              <button
                key={d}
                className={`aico__windowBtn ${days === d ? 'active' : ''}`}
                onClick={() => setDays(d)}
              >
                {d}T
              </button>
            ))}
          </div>
          <button className="aico__refresh" onClick={() => load(days)} disabled={isLoading}>
            <i className={`fa fa-rotate ${isLoading ? 'fa-spin' : ''}`} /> Aktualisieren
          </button>
        </div>
      </div>

      {error && (
        <div className="aico__error">
          <i className="fa fa-triangle-exclamation" /> {error}
        </div>
      )}

      <div className="aico__cards">
        <div className="aico__card">
          <div className="aico__cardLabel">Aktive Accounts</div>
          <div className="aico__cardValue">{totals.accounts}</div>
        </div>
        <div className="aico__card aico__card--primary">
          <div className="aico__cardLabel">Geschätzte KI-Kosten ({days}T)</div>
          <div className="aico__cardValue">{eur(totals.estimatedCostEur)}</div>
        </div>
        <div className="aico__card">
          <div className="aico__cardLabel">Verbrauchte Credits</div>
          <div className="aico__cardValue">{(totals.creditsSpent || 0).toLocaleString('de-DE')}</div>
        </div>
        <div className="aico__card">
          <div className="aico__cardLabel">KI-Aktionen</div>
          <div className="aico__cardValue">{(totals.events || 0).toLocaleString('de-DE')}</div>
        </div>
      </div>

      {sortedProviders(totals.byProvider).length > 0 && (
        <div className="aico__providerStrip">
          <span className="aico__providerStripLabel">Kosten pro KI-Anbieter:</span>
          <ProviderChips byProvider={totals.byProvider} />
        </div>
      )}

      {topAccount && topAccount.estimatedCostEur > 0 && (
        <div className="aico__top">
          <i className="fa fa-fire" />
          <span>
            Top-Verbraucher: <strong>{topAccount.name || topAccount.email || topAccount.uid}</strong>{' '}
            mit geschätzt <strong>{eur(topAccount.estimatedCostEur)}</strong> in {days} Tagen.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="aico__loading">
          <i className="fa fa-spinner fa-spin" /> Lade KI-Kosten…
        </div>
      ) : accounts.length === 0 ? (
        <div className="aico__empty">Keine aktiven Accounts mit KI-Verbrauch im Zeitfenster.</div>
      ) : (
        <div className="aico__tableWrap">
          <table className="aico__table">
            <thead>
              <tr>
                <th />
                <th>#</th>
                <th>Account</th>
                <th>KI-Anbieter</th>
                <th>Plan</th>
                <th>Last Access</th>
                <th className="aico__num">Aktionen</th>
                <th className="aico__num">Credits ({days}T)</th>
                <th className="aico__num">Geschätzte KI-Kosten ({days}T)</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, idx) => {
                const isOpen = !!expanded[a.uid];
                const breakdownRows = Object.entries(a.breakdown || {}).sort(
                  (x, y) => (y[1].eur || 0) - (x[1].eur || 0)
                );
                const barPct = maxCost > 0 ? Math.max(2, (a.estimatedCostEur / maxCost) * 100) : 0;
                return (
                  <React.Fragment key={a.uid}>
                    <tr
                      className={`aico__row ${isOpen ? 'open' : ''} ${idx === 0 ? 'top' : ''}`}
                      onClick={() => toggle(a.uid)}
                    >
                      <td className="aico__expandCell">
                        <i className={`fa fa-chevron-${isOpen ? 'down' : 'right'}`} />
                      </td>
                      <td className="aico__rank">{idx + 1}</td>
                      <td>
                        <div className="aico__account">
                          <span className="aico__accountName">{a.name || a.companyName || '—'}</span>
                          <span className="aico__accountEmail">{a.email || a.uid}</span>
                        </div>
                      </td>
                      <td>
                        <ProviderChips byProvider={a.byProvider} />
                      </td>
                      <td>
                        {a.plan?.plan ? (
                          <span className={`aico__plan aico__plan--${a.plan.plan}`}>{a.plan.plan}</span>
                        ) : (
                          <span className="aico__plan aico__plan--none">kein Plan</span>
                        )}
                      </td>
                      <td className="aico__date">{fmtDate(a.lastAccess)}</td>
                      <td className="aico__num">{a.eventCount}</td>
                      <td className="aico__num">{(a.creditsSpent || 0).toLocaleString('de-DE')}</td>
                      <td className="aico__num aico__costCell">
                        <div className="aico__costWrap">
                          <span className="aico__cost">{eur(a.estimatedCostEur)}</span>
                          <span className="aico__bar" style={{ width: `${barPct}%` }} />
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="aico__detailRow">
                        <td colSpan={9}>
                          {breakdownRows.length === 0 ? (
                            <div className="aico__noBreakdown">Keine geschätzten KI-Kosten in diesem Zeitfenster.</div>
                          ) : (
                            <div className="aico__detailGrid">
                              <div className="aico__detailCol">
                                <h4 className="aico__detailTitle">
                                  <i className="fa fa-users" /> Pro Mitarbeiter
                                </h4>
                                <table className="aico__breakdown">
                                  <thead>
                                    <tr>
                                      <th>Mitarbeiter</th>
                                      <th className="aico__num">Aktionen</th>
                                      <th className="aico__num">Credits</th>
                                      <th className="aico__num">Geschätzte Kosten</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(a.members || []).map((m) => (
                                      <tr key={m.uid}>
                                        <td>
                                          <div className="aico__memberCell">
                                            <span className="aico__memberName">
                                              {m.name || m.email || m.uid}
                                              {m.isOwner && <span className="aico__ownerTag">Owner</span>}
                                            </span>
                                            {m.email && m.name && (
                                              <span className="aico__memberEmail">{m.email}</span>
                                            )}
                                            <ProviderChips byProvider={m.byProvider} />
                                          </div>
                                        </td>
                                        <td className="aico__num">{m.eventCount}</td>
                                        <td className="aico__num">{(m.creditsSpent || 0).toLocaleString('de-DE')}</td>
                                        <td className="aico__num">{eur(m.estimatedCostEur)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              <div className="aico__detailCol">
                                <h4 className="aico__detailTitle">
                                  <i className="fa fa-list" /> Pro KI-Aktion
                                </h4>
                                <table className="aico__breakdown">
                                  <thead>
                                    <tr>
                                      <th>KI-Aktion</th>
                                      <th className="aico__num">Anzahl</th>
                                      <th className="aico__num">Credits</th>
                                      <th className="aico__num">Geschätzte Kosten</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownRows.map(([action, b]) => (
                                      <tr key={action}>
                                        <td>{labelFor(action)}</td>
                                        <td className="aico__num">{b.count}</td>
                                        <td className="aico__num">{(b.credits || 0).toLocaleString('de-DE')}</td>
                                        <td className="aico__num">{eur(b.eur)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <p className="aico__footnote">
          Schätzung auf Basis von {data.accounts.length} Account(s) · Kurs {data.usdToEur} USD/EUR ·
          erzeugt {fmtDate(data.generatedAt)}. Annahmen anpassbar in{' '}
          <code>functions/aiCostEstimates.js</code>.
        </p>
      )}
    </div>
  );
}

export default AiCostOverview;
